/**
 * @fullvsl/contracts — FullVSL MVP shared contracts (Phase 0)
 *
 * Single source of truth for: VSL config, overlays, delayed CTA, resume play,
 * analytics events, player event API, REST API request/response shapes, the
 * embed/postMessage surface, and the Full Funnel integration adapter boundary.
 *
 * Formalized from the approved MVP Architecture & Execution Plan (FUL-1, plan §3).
 * All shapes are the canonical reference every package integrates against.
 * Missing external access is covered by documented mock/fixture paths — nothing
 * here depends on Full Funnel credentials.
 *
 * Conventions:
 *  - `id`/uuid fields are strings (uuid v4 unless noted).
 *  - Timestamps are ISO-8601 strings (UTC) on the wire; `Sec` suffix = seconds (number).
 *  - Enum unions are string-literal unions; senders MUST emit one of the listed values.
 *  - `?` = optional. Optional analytics fields default per the AnalyticsEvent section.
 *  - Stable + additive-only. Breaking changes require a contracts revision + CTO review.
 */

/* =====================================================================================
 * 0. Common primitives
 * ===================================================================================== */

/** UUID v4 (or server-assigned sortable id for VSL/project entities). */
export type ID = string;

/** ISO-8601 UTC timestamp, e.g. "2026-07-02T08:14:35.812Z". */
export type ISODate = string;

/** A normalized, CDN-backed media resource resolved from a source. */
export interface MediaResult {
  /** Direct playable URL (MP4 for fullfunnel_media / direct_url). */
  url: string;
  /** Mime type, e.g. "video/mp4". */
  mimeType: "video/mp4" | "video/webm" | string;
  /** Duration in seconds, when known. */
  durationSec?: number;
  /** Natural pixel dimensions, when known (drives aspect-ratio auto-detect). */
  width?: number;
  height?: number;
  /** True if the host sends Access-Control-Allow-Origin (required for direct <video>). */
  corsEnabled?: boolean;
  /** True if the host honors byte-range requests (required for seeking). */
  rangeSupported?: boolean;
}

/* =====================================================================================
 * 1. VSL Project (plan §3.1)
 * ===================================================================================== */

export type SourceType = "fullfunnel_media" | "direct_url" | "youtube";

export type AspectRatio =
  | "16:9"
  | "9:16"
  | "1:1"
  | "4:5"
  | "3:4"
  | "custom"
  | "auto";

export type VslStatus = "draft" | "published";

/** Smart autoplay configuration (PRD §7.3). */
export interface AutoplayConfig {
  /** Whether autoplay should be attempted at all. Defaults to true. */
  enabled?: boolean;
  /** Attempt autoplay on load (muted browsers will block unmuted autoplay). */
  muted: boolean;
  /** Show a click-to-unmute / "tap for sound" prompt when autoplay starts muted. */
  promptToUnmute: boolean;
  /** Track autoplay impressions as a distinct event separate from play. */
  trackSeparately: boolean;
}

export type ProgressBarMode = "linear" | "accelerated";

/** Visual progress behavior. Playback and analytics always use real media time. */
export interface ProgressBarConfig {
  mode: ProgressBarMode;
  /** Ease-out exponent. 1 is linear; higher values advance faster near the start. */
  intensity: number;
}

export const DEFAULT_PROGRESS_BAR_CONFIG: ProgressBarConfig = {
  mode: "accelerated",
  intensity: 1.6,
};

export interface VslProject {
  id: ID;
  name: string;
  status: VslStatus;
  sourceType: SourceType;
  sourceUrl: string;
  aspectRatio: AspectRatio;
  /** Required when aspectRatio === "custom". */
  customRatio?: { w: number; h: number };
  /** Full Funnel linkage; optional until integration is wired. */
  funnelId?: string;
  pageId?: string;
  locationId?: string;
  autoplay: AutoplayConfig;
  progressBar: ProgressBarConfig;
  cta: CtaConfig;
  overlays: Overlay[];
  delays: DelayedElement[];
  resume: ResumeConfig;
  /** Per-VSL accent used by player controls, resume prompts, and CTA UI. */
  brandColor: string;
  /** Mirror normalized player events to the embedding Full Funnel window. */
  emitAnalyticsToParent: boolean;
  publish: PublishConfig;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface PublishConfig {
  /** Embed script URL, e.g. https://{host}/embed/fullvsl.js */
  embedScript: string;
  /** Approved target funnel page URL where the embed is installed, once known. */
  targetUrl?: string;
}

/* =====================================================================================
 * 2. CTA — watched-progress based (plan §3.2)
 * ===================================================================================== */

export type CtaRevealMode = "timestamp" | "percent";

/**
 * What the reveal threshold is measured against.
 * CTO default = "maxWatched" so rewinding cannot re-hide / game the CTA.
 * Product Lead may confirm either; both are shippable.
 */
export type CtaBasedOn = "currentTime" | "maxWatched";

export type CtaDestinationType =
  | "url"
  | "funnel_checkout"
  | "form"
  | "calendar"
  | "js_event"
  | "css_reveal";

export interface CtaDestination {
  type: CtaDestinationType;
  /** URL, selector, or event name depending on `type`. */
  value: string;
}

export interface CtaConfig {
  revealMode: CtaRevealMode;
  /** Seconds (timestamp) or percent 0-100 (percent). */
  revealAt: number;
  basedOn: CtaBasedOn;
  /** If true, scrubbing/rewinding past the reveal point hides the CTA again. */
  rehideOnRewind: boolean;
  /** If true, the CTA stays visible for the remainder of the session once revealed. */
  persistAfterReveal: boolean;
  destination: CtaDestination;
}

/* =====================================================================================
 * 3. Overlay (plan §3.3)
 * ===================================================================================== */

export type OverlayType =
  | "start"
  | "autoplay"
  | "minihook"
  | "pause"
  | "cta_reveal"
  | "end";

export type OverlayShowMoment =
  | "immediate"
  | "timestamp"
  | "duration"
  | "onPause"
  | "onReplay";

export type OverlayVisibility = "all" | "desktop" | "mobile";

export type OverlayAction =
  | "continue"
  | "restart"
  | "jump_to_offer"
  | "open_cta"
  | "close";

export interface OverlayTiming {
  show: OverlayShowMoment;
  /** Seconds from start (timestamp) or show moment. */
  at?: number;
  /** How long the overlay stays, in milliseconds. */
  durationMs?: number;
}

export interface OverlayContent {
  text?: string;
  subtext?: string;
  buttonLabel?: string;
  buttonAction?: string;
  assetUrl?: string;
  assetType?: "image" | "video" | "html";
  position?: "top" | "center" | "bottom" | "top_left" | "top_right" | "bottom_left" | "bottom_right";
  font?: string;
  colors?: {
    background?: string;
    text?: string;
    button?: string;
    buttonText?: string;
  };
  bgOpacity?: number; // 0..1
  animation?: "none" | "fade" | "slide" | "zoom";
  visibility?: OverlayVisibility;
}

export interface Overlay {
  id: ID;
  type: OverlayType;
  timing: OverlayTiming;
  content: OverlayContent;
  actions: OverlayAction[];
}

/* =====================================================================================
 * 4. Delayed page element (plan §3.4)
 * ===================================================================================== */

export type DelayedTrigger =
  | "timestamp"
  | "percent"
  | "ctaClick"
  | "pause"
  | "complete";

export interface DelayedElement {
  /** CSS selector on the host page that the embed will show/toggle. */
  cssSelector: string;
  trigger: DelayedTrigger;
  /** Seconds (timestamp) or percent 0-100 (percent); ignored for ctaClick/pause/complete. */
  at: number;
}

/* =====================================================================================
 * 5. Resume-play (plan §3.7) — client-side persistence contract
 * ===================================================================================== */

export type ResumeMode = "ask" | "always_resume" | "always_restart";

export interface ResumeConfig {
  mode: ResumeMode;
  /** Minimum watched seconds before this VSL offers resume play. */
  thresholdSec?: number;
}

/**
 * Resume state persisted client-side (LocalStorage with cookie fallback).
 * Keyed deterministically so a returning visitor is recognized without login.
 *
 * Storage key = `fullvsl:resume:{videoId|sourceHash}` where the composite key is
 * `{videoId, funnelId|pageId, sourceHash}` — implementations MUST hash/serialize
 * the composite deterministically (see resumeKey() in resume-schema.ts).
 *
 * Privacy-light: this blob carries NO PII — only the composite key, playback
 * position/maxWatched/duration, the CTA-shown flag, and an updatedAt timestamp.
 * No email, contact id, or Full Funnel identity is stored client-side. The
 * server-side ResumeStore is an OPTIONAL cross-device path behind the same
 * interface (see resume-schema.ts §D + api-routes /resume).
 */
export interface ResumeState {
  /** Composite-derived key (stable across visits for the same visitor+video). */
  key: string;
  videoId: ID;
  /** Visitor this state belongs to (scoped per-browser; needed by the server-side path). */
  visitorId?: string;
  funnelId?: string;
  pageId?: string;
  /** Hash of sourceUrl to detect media swaps under the same id. */
  sourceHash: string;
  /** Last known playback position, seconds (the "last timestamp" checkpoint). */
  positionSec: number;
  /** Greatest contiguous progress reached, seconds (anti-gaming, mirrors maxWatched). */
  maxWatchedSec: number;
  /** Video duration at last touch, seconds (to compute pct threshold for "worth resuming"). */
  durationSec?: number;
  /**
   * Whether the CTA was already revealed in a prior session (FUL-9 wiring).
   * Persisted so a returning visitor whose CTA was shown does not need to
   * re-watch to the reveal threshold — the player re-shows it immediately.
   * Monotonic: once true it stays true until the state is cleared.
   */
  ctaShown?: boolean;
  /** ISO timestamp of when the CTA was first revealed (audit), when known. */
  ctaShownAt?: ISODate;
  /** ISO timestamp of the last resume checkpoint. */
  updatedAt: ISODate;
}

/** Minimum watched progress before a resume prompt is offered (default 5s / 3%). */
export interface ResumeThresholds {
  minSeconds: number;
  minPercent: number;
}
export const DEFAULT_RESUME_THRESHOLDS: ResumeThresholds = {
  minSeconds: 5,
  minPercent: 3,
};

/* =====================================================================================
 * 6. Player Event API (normalized surface emitted by @fullvsl/player)
 *
 * The player emits normalized PlayerEvents on a small dispatcher (callback +
 * postMessage). The analytics SDK batches/translate these to AnalyticsEvent and
 * POSTs to the API. Both surfaces share an event-name taxonomy so translation is
 * a 1:1 mapping with enrichment.
 * ===================================================================================== */

export type PlayerEventName =
  | "impression"
  | "autoplay_impression"
  | "play"
  | "pause"
  | "progress"
  | "milestone"
  | "completed"
  | "seek"
  | "cta_shown"
  | "cta_clicked"
  | "resume_offered"
  | "resume_accepted"
  | "resume_declined"
  | "pause_overlay_shown"
  | "pause_overlay_action"
  | "offer_moment_reached"
  | "error";

export interface PlayerEventBase {
  /** Stable, monotonically-ish increasing sequence within a session (de-dup aid). */
  seq: number;
  name: PlayerEventName;
  /** Client clock at emit time (ISO-8601). Server also stamps serverTs on ingest. */
  clientTs: ISODate;
  vslId: ID;
  sessionId: string;
  visitorId: string;
}

export interface PlayerProgressEvent extends PlayerEventBase {
  name: "progress";
  /** Current clock position, seconds. */
  positionSec: number;
  /** Greatest contiguous progress reached, seconds (maxWatched). */
  maxWatchedSec: number;
  /** Duration, seconds. */
  durationSec: number;
  /** Current position as percent 0-100 (2dp). */
  pct: number;
}

export interface PlayerMilestoneEvent extends PlayerEventBase {
  name: "milestone";
  /** One of 25 / 50 / 75 / 100. */
  milestone: 25 | 50 | 75 | 100;
  positionSec: number;
  durationSec: number;
}

export interface PlayerSeekEvent extends PlayerEventBase {
  name: "seek";
  fromSec: number;
  toSec: number;
  maxWatchedSec: number;
  durationSec: number;
}

export interface PlayerCtaEvent extends PlayerEventBase {
  name: "cta_shown" | "cta_clicked";
  /** reveal mode used to trigger, for analytics slicing. */
  revealMode: CtaRevealMode;
  basedOn: CtaBasedOn;
  positionSec: number;
  destinationType: CtaDestinationType;
}

export interface PlayerResumeEvent extends PlayerEventBase {
  name: "resume_offered" | "resume_accepted" | "resume_declined";
  /** Position that would be / was restored. */
  positionSec: number;
  maxWatchedSec: number;
}

export interface PlayerOverlayEvent extends PlayerEventBase {
  name: "pause_overlay_shown" | "pause_overlay_action";
  overlayId?: ID;
  action?: OverlayAction;
  positionSec: number;
}

export interface PlayerOfferMomentEvent extends PlayerEventBase {
  name: "offer_moment_reached";
  positionSec: number;
  /** True if reached via organic viewing vs. a seek. */
  organic: boolean;
}

export interface PlayerErrorEvent extends PlayerEventBase {
  name: "error";
  code: string;
  message: string;
  sourceType: SourceType;
}

export interface PlayerSimpleEvent extends PlayerEventBase {
  name:
    | "impression"
    | "autoplay_impression"
    | "play"
    | "pause"
    | "completed";
  positionSec: number;
  durationSec: number;
}

export type PlayerEvent =
  | PlayerSimpleEvent
  | PlayerProgressEvent
  | PlayerMilestoneEvent
  | PlayerSeekEvent
  | PlayerCtaEvent
  | PlayerResumeEvent
  | PlayerOverlayEvent
  | PlayerOfferMomentEvent
  | PlayerErrorEvent;

/**
 * Dispatcher the player calls. Implementations: in-page callback, postMessage to
 * host, and the analytics SDK adapter that batches + POSTs.
 */
export type PlayerEventDispatcher = (event: PlayerEvent) => void;

/* =====================================================================================
 * 7. Analytics event — ingestion wire model (plan §3.5)
 * ===================================================================================== */

export type AnalyticsEventType = PlayerEventName;

export interface AnalyticsEventMeta {
  positionSec?: number;
  pct?: number;
  source?: SourceType;
  device?: "desktop" | "mobile" | "tablet";
  browser?: string;
  country?: string;
  utm?: { source?: string; medium?: string; campaign?: string; term?: string; content?: string };
  aspectRatio?: AspectRatio;
  /** Distinguishes native HTML5 player vs YouTube IFrame mode. */
  sourceMode?: "html5" | "youtube";
  milestone?: 25 | 50 | 75 | 100;
  [k: string]: unknown;
}

export interface AnalyticsEvent {
  /** UUID v4, idempotency key — server dedupes by this. */
  eventId: ID;
  vslId: ID;
  sessionId: string;
  visitorId: string;
  type: AnalyticsEventType;
  /** Client clock at emit. */
  clientTs: ISODate;
  /** Set by server on receipt. */
  serverTs?: ISODate;
  /** Optional scalar payload (e.g. milestone percent, seek delta). */
  value?: number;
  meta?: AnalyticsEventMeta;
}

/** Batch ingest request (POST /api/v1/events). */
export interface IngestEventsRequest {
  events: AnalyticsEvent[];
}

/** Per-event ingest result; order matches the request. */
export interface IngestEventResult {
  eventId: ID;
  /** "stored" | "duplicate" (idempotent dedup by eventId). */
  status: "stored" | "duplicate";
}
export interface IngestEventsResponse {
  results: IngestEventResult[];
  /** Count of newly stored events (duplicates excluded). */
  stored: number;
  duplicate: number;
}

/**
 * Throttling rules (player-side) — the player MUST throttle before emitting:
 *  - progress: at most every 1s AND every 5% of duration, whichever is later.
 *  - milestones: exactly once each at 25/50/75/100 (use maxWatched, not currentTime).
 * The server is resilient to late/repeat events (dedup by eventId).
 */
export const PROGRESS_THROTTLE = {
  minIntervalMs: 1000,
  minPctStep: 5,
} as const;
export const MILESTONES = [25, 50, 75, 100] as const;

/* =====================================================================================
 * 8. Full Funnel event mapping (PRD §11.3)
 *
 * Maps AnalyticsEventType → Full Funnel tag/workflow event name. The adapter uses
 * this when dispatching; with no credentials it logs (mock).
 * ===================================================================================== */

export const FULLFUNNEL_EVENT_MAP: Record<string, string> = {
  impression: "fullvsl_view",
  play: "fullvsl_play",
  pause: "fullvsl_pause",
  milestone: "fullvsl_watched", // suffixed with milestone at dispatch, e.g. watched_25
  completed: "fullvsl_completed",
  cta_shown: "fullvsl_cta_shown",
  cta_clicked: "fullvsl_cta_clicked",
  resume_accepted: "fullvsl_resume_accepted",
  offer_moment_reached: "fullvsl_offer_moment_reached",
};

/* =====================================================================================
 * 9. REST API request/response shapes (plan §3.6)
 * ===================================================================================== */

/** Batch ingest. */
// (IngestEventsRequest / IngestEventsResponse defined in §7.)

/** VSL project CRUD. */
export interface CreateVslRequest {
  name: string;
  sourceType: SourceType;
  sourceUrl: string;
  aspectRatio: AspectRatio;
  customRatio?: { w: number; h: number };
  autoplay?: Partial<AutoplayConfig>;
  progressBar?: Partial<ProgressBarConfig>;
  cta?: Partial<CtaConfig>;
  resume?: Partial<ResumeConfig>;
  brandColor?: string;
  emitAnalyticsToParent?: boolean;
}
export type UpdateVslRequest = Partial<
  Pick<VslProject, "name" | "status" | "sourceType" | "sourceUrl" | "aspectRatio" | "customRatio" | "autoplay" | "progressBar" | "cta" | "resume" | "brandColor" | "emitAnalyticsToParent" | "funnelId" | "pageId" | "locationId">
>;

/** PUT /api/v1/vsls/:id/config — overlays/cta/delays/resume/aspect. */
export interface UpdateVslConfigRequest {
  overlays?: Overlay[];
  cta?: CtaConfig;
  delays?: DelayedElement[];
  resume?: ResumeConfig;
  autoplay?: AutoplayConfig;
  progressBar?: ProgressBarConfig;
  brandColor?: string;
  emitAnalyticsToParent?: boolean;
  aspectRatio?: AspectRatio;
  customRatio?: { w: number; h: number };
}

/** Media validation result (POST /api/v1/vsls/:id/validate-media). */
export interface MediaValidationRequest {
  sourceType: SourceType;
  sourceUrl: string;
}
export interface MediaValidationResult {
  ok: boolean;
  media?: MediaResult;
  /** Machine-readable failure reason when ok=false. */
  reason?: "cors" | "no_range" | "not_found" | "wrong_type" | "unreachable" | "youtube_unsupported";
  message?: string;
  checkedAt: ISODate;
}

/** Analytics overview (GET /api/v1/vsls/:id/analytics). */
export interface AnalyticsOverview {
  vslId: ID;
  windowStart: ISODate;
  windowEnd: ISODate;
  impressions: number;
  plays: number;
  /** plays / impressions (unique visitors). */
  playRate: number;
  /** Average watch-through percent. */
  avgWatchPct: number;
  /** Retention curve sampled at 0,10,…,100. */
  retentionCurve: { pct: number; retained: number }[];
  ctaShown: number;
  ctaClicked: number;
  /** cta_clicked / cta_shown (conversion proxy, PRD §10.4). */
  ctaClickRate: number;
  resumeOffered: number;
  resumeAccepted: number;
  /**
   * Revenue metrics (Phase 2, feature-flagged).
   * Present only when revenue tracking is enabled for this VSL.
   * CTO condition 3: optional — existing API consumers see no change.
   */
  revenue?: import("./phase2-analytics-contracts").RevenueMetrics;
}

/** Breakdown (GET /api/v1/vsls/:id/analytics/breakdown). */
export interface AnalyticsBreakdown {
  vslId: ID;
  bySource: Record<string, number>;
  byDevice: Record<string, number>;
  byAspectRatio: Record<string, number>;
}

/* =====================================================================================
 * 10. Player embed contract (plan §3.7)
 * ===================================================================================== */

/**
 * Host page embed:
 *   <script src="https://{host}/embed/fullvsl.js" async></script>
 *   <div data-fullvsl="{vslId}" data-context="{encoded FF context}"></div>
 *
 * `data-context` is the Full Funnel context blob (opaque to the player; parsed by
 * the integration adapter). With no credentials it may be empty — the player still
 * runs with a local visitorId.
 */
export interface EmbedInit {
  vslId: ID;
  /** Opaque Full Funnel context (base64/JSON) from the host page. */
  context?: string;
}

/** postMessage bridge between the embed iframe and the host page. */
export interface EmbedPostMessage {
  /** Always "fullvsl" so the host can scope listeners. */
  source: "fullvsl";
  vslId: ID;
  event: PlayerEvent;
}

/* =====================================================================================
 * 11. Integration adapter boundary (plan §3.8)
 *
 * Behind this interface: mock now (logs/fixtures), real when credentials land.
 * No FE/BE/analytics code depends on credentials.
 * ===================================================================================== */

export interface FFContext {
  locationId?: string;
  userId?: string;
  contactId?: string;
  name?: string;
  email?: string;
  phone?: string;
  /** Raw opaque blob for forward-compat. */
  raw?: Record<string, unknown>;
}

export type DispatchChannel = "tag" | "workflow" | "webhook" | "opportunity";

export interface DispatchResult {
  ok: boolean;
  channel: DispatchChannel;
  /** External id returned by Full Funnel, when available. */
  externalId?: string;
  /** Present when ok=false (e.g. credentials missing → expected in mock mode). */
  error?: string;
  /** True when handled by the mock (no real call made). */
  mock: boolean;
}

export interface FullFunnelAdapterStatus {
  live: boolean;
  scopes: string[];
  /** Why it is not live, when applicable (e.g. "no credentials"). */
  reason?: string;
}

export interface FullFunnelAdapter {
  /** Parse the opaque host context into structured fields. */
  parseContext(query: string | Record<string, unknown>): FFContext;
  /** Resolve a media URL for a location/query (mock returns the fixture MP4). */
  resolveMedia(locationId?: string, query?: Record<string, unknown>): MediaResult;
  /** Fan a mapped event to Full Funnel tags/workflows/webhooks (mock logs). */
  dispatchEvent(event: AnalyticsEvent, mapping: Record<string, string>): DispatchResult;
  /**
   * Full multi-channel fan-out per plan FUL-14 §5 (FUL-15). Returns the
   * per-channel results plus the GHL entities created/applied (tag, workflow,
   * opportunity). Optional + additive: the basic `dispatchEvent` stays the
   * single-result convenience; this is the inspectable, entity-recording path
   * QA and the §9 V5/V6 validation run against. The real adapter implements the
   * same fan-out against live GHL APIs; the mock records fixtures in-memory.
   */
  dispatchEventFanout?(
    event: AnalyticsEvent,
    mapping: Record<string, string>,
  ): DispatchFanoutResult;
  /**
   * Resolve a Full Funnel contact from context (FUL-15). The mock returns/creates
   * a fixture contact keyed by `contactId`; the real adapter calls `contacts.read`.
   * Used so dispatched tags/opportunities attach to the right contact.
   */
  resolveContact?(context: FFContext): FfContact;
  /** Whether the adapter is live or running in mock mode. */
  getStatus(): FullFunnelAdapterStatus;
  /**
   * Verify a GHL webhook signature against the raw request body (FUL-51).
   * Mock mode: shared-secret HMAC-SHA256. Real GHL scheme confirmed at the gate.
   */
  verifyWebhookSignature(
    rawBody: string,
    signature: string | undefined,
  ): Promise<WebhookVerificationResult>;
  /**
   * Simulate GHL echoing a dispatched analytics event back as an inbound webhook
   * (mock round-trip, FUL-51). Real GHL fires these organically; this is the
   * mock-startable path so the §9 V8 webhook round-trip has a concrete target
   * before credentials land. Returns the normalized event + the mock signature.
   */
  emitRoundTripWebhook(
    event: AnalyticsEvent,
  ): Promise<{ event: GhlWebhookEvent; signature: string }>;
}

/* =====================================================================================
 * 12. Webhook receiver (GHL → FullVSL inbound) — FUL-51 / FUL-16 / plan §10
 *
 * GHL outbound webhooks (contact/workflow/opportunity lifecycle events, plus the
 * echo of dispatched fullvsl_* events) hit the `/webhooks/ghl` receiver. In mock
 * mode the signature is a shared-secret HMAC-SHA256 the mock adapter produces on
 * dispatch; at the gate the real GHL signature header scheme is verified
 * (documented in the FUL-2 access packet; do not block on it now).
 * ===================================================================================== */

/** GHL webhook signature header (documented; exact header confirmed at gate). */
export const GHL_WEBHOOK_SIGNATURE_HEADER = "x-ghl-signature";

/** Mock shared-secret env var name (never stored in code; documented for the gate). */
export const GHL_WEBHOOK_SECRET_ENV = "FULLVSL_GHL_WEBHOOK_SECRET";

/** Documented mock secret used when the env var is absent (tests/dev only). */
export const MOCK_WEBHOOK_SECRET = "fullvsl-mock-webhook-secret";

/** Result of verifying a webhook signature. */
export interface WebhookVerificationResult {
  ok: boolean;
  /** Why verification failed, when ok=false. */
  reason?: "missing_signature" | "bad_signature" | "no_secret";
}

/** An inbound GHL webhook event payload, normalized for recording/dispatch. */
export interface GhlWebhookEvent {
  /** Idempotency key — GHL event id, or a derived stable id (dedup by this). */
  id: string;
  /** GHL event type, e.g. "contact.create", or the echoed fullvsl_* type. */
  type: string;
  locationId?: string;
  contactId?: string;
  /** The originating analytics eventId when this is an echo of a dispatch. */
  sourceEventId?: ID;
  /** Full raw payload for replay/audit. */
  payload: Record<string, unknown>;
  /** ISO timestamp GHL emitted (from payload, else receivedAt fallback). */
  emittedAt?: ISODate;
}

/** A recorded webhook event (append-only, idempotent by id). */
export interface WebhookEventRecord extends GhlWebhookEvent {
  /** ISO timestamp FullVSL received it. */
  receivedAt: ISODate;
  /** "stored" the first time, "duplicate" on idempotent re-delivery. */
  status: "stored" | "duplicate";
  /** Whether signature verification passed. */
  signatureValid: boolean;
}

/** Per-event ingest result for the webhook receiver. */
export interface WebhookIngestResult {
  id: string;
  status: "stored" | "duplicate";
  signatureValid: boolean;
}

/* =====================================================================================
 * 13. Full Funnel entity fan-out (FUL-15 / plan FUL-14 §4–§5)
 *
 * The webhook/event *dispatch receiver* (GHL → FullVSL) is §12. This section is
 * the *outbound* side: a dispatched analytics event fans out to one or more GHL
 * entities — a tag applied to the contact, a workflow triggered, and (for the
 * conversion proxy) an opportunity created. The mock records these in-memory so
 * the MVP is fully exercisable without credentials; the real adapter performs
 * the identical fan-out against live GHL APIs (contacts/tags/workflows/
 * opportunities). Entity shapes are the mock-recordable contract; the real
 * adapter returns the same shapes with real GHL ids.
 * ===================================================================================== */

/** A GHL contact resolved from context (mock = fixture; real = contacts.read). */
export interface FfContact {
  id: string;
  locationId?: string;
  name?: string;
  email?: string;
  phone?: string;
}

/** A tag applied to a contact as the result of a dispatched event. */
export interface FfTag {
  /** Stable id: `{contactId}:{tagName}` — idempotent re-application dedupes. */
  id: string;
  /** The mapped GHL tag name, e.g. `fullvsl_play`, `fullvsl_watched_25`. */
  name: string;
  contactId?: string;
  locationId?: string;
  /** Originating analytics eventId that caused the tag (audit/idempotency). */
  sourceEventId: ID;
  appliedAt: ISODate;
}

/** A workflow trigger fired as the result of a dispatched event. */
export interface FfWorkflowTrigger {
  id: string;
  /** The mapped GHL workflow trigger event name (== tag name convention). */
  workflowName: string;
  contactId?: string;
  locationId?: string;
  sourceEventId: ID;
  triggeredAt: ISODate;
}

/** An opportunity created as the conversion proxy for `cta_clicked` (PRD §10.4). */
export interface FfOpportunity {
  id: string;
  name: string;
  contactId?: string;
  locationId?: string;
  /** Pipeline/stage is a gate-time config detail; mock leaves it generic. */
  pipelineStage?: string;
  sourceEventId: ID;
  createdAt: ISODate;
}

/**
 * Per-event-type fan-out channels (plan FUL-14 §5). Which GHL entities a
 * dispatched event produces. The mock applies exactly these; the real adapter
 * mirrors them. `webhook` is covered separately by §12 (inbound) and the mock's
 * `emitRoundTripWebhook` (outbound echo), so it is intentionally absent here.
 *
 *   impression / autoplay_impression / play / pause / cta_shown → tag
 *   milestone / completed → tag + workflow (milestone automations)
 *   cta_clicked → tag + workflow + opportunity (conversion proxy)
 *   resume_accepted / offer_moment_reached → tag + workflow
 *   progress / seek / resume_offered / resume_declined / overlay / error → none
 */
export const DISPATCH_FANOUT_RULES: Record<string, DispatchChannel[]> = {
  impression: ["tag"],
  autoplay_impression: ["tag"],
  play: ["tag"],
  pause: ["tag"],
  progress: [],
  milestone: ["tag", "workflow"],
  completed: ["tag", "workflow"],
  seek: [],
  cta_shown: ["tag"],
  cta_clicked: ["tag", "workflow", "opportunity"],
  resume_offered: [],
  resume_accepted: ["tag", "workflow"],
  resume_declined: [],
  pause_overlay_shown: [],
  pause_overlay_action: [],
  offer_moment_reached: ["tag", "workflow"],
  error: [],
};

/** Result of a single event's multi-channel fan-out (FUL-15). */
export interface DispatchFanoutResult {
  /** The originating analytics eventId (idempotency key for the fan-out). */
  eventId: ID;
  /** The mapped GHL name for this event (tag/workflow/opportunity share it). */
  mappedName: string;
  /** Channels that fired for this event (from DISPATCH_FANOUT_RULES). */
  channels: DispatchChannel[];
  /** Per-channel dispatch results (one entry per fired channel). */
  results: DispatchResult[];
  /** The tag applied, when the `tag` channel fired. */
  tag?: FfTag;
  /** The workflow triggered, when the `workflow` channel fired. */
  workflow?: FfWorkflowTrigger;
  /** The opportunity created, when the `opportunity` channel fired. */
  opportunity?: FfOpportunity;
  /** The contact the entities attached to (resolved from context/meta), if any. */
  contact?: FfContact;
}

/* =====================================================================================
 * 14. Self-service per-subaccount connection (FUL-128)
 *
 * The operator publishes ONE global Custom Menu Link to FullVSL. Each subaccount
 * admin connects their OWN Full Funnel / HighLevel location inside FullVSL using
 * a private API key or OAuth/private-integration flow they control. There is NO
 * central Marco-owned Full Funnel credential. This section defines the tenant
 * connection lifecycle: connect → test → (use) → rotate → revoke, with strict
 * location isolation, redacted errors, and no client-side secret persistence.
 *
 * The `ConnectionManager` is the boundary. A mock implementation lets the full
 * integration surface run locally; a real implementation encrypts the credential
 * at rest (envelope encryption) and proxies scoped GHL calls per location. The
 * API layer (§9) depends on the interface; the wiring is a factory swap.
 * ===================================================================================== */

/**
 * Required Full Funnel / HighLevel scopes for FullVSL MVP (from FUL-2 access
 * packet §2 / FUL-15 §5). The connection test verifies the supplied credential
 * grants at least these scopes for the target location.
 */
export const REQUIRED_FF_SCOPES = [
  "files.read",
  "contacts.read",
  "contacts.write",
  "tags.read",
  "tags.write",
  "opportunities.write",
  "workflows",
] as const;

export type RequiredFfScope = (typeof REQUIRED_FF_SCOPES)[number];

/** Lifecycle status of a per-subaccount connection. */
export type ConnectionStatus = "pending" | "connected" | "error" | "revoked";

/**
 * A masked representation of the stored credential. NEVER includes the raw
 * secret/token value — only a fingerprint for display ("••••1234") and a hash
 * for dedup/comparison. This is the ONLY shape returned by any read/status API.
 */
export interface RedactedCredential {
  /** Display-safe mask, e.g. "pit-••••-d7a1" (first/last chars, rest redacted). */
  masked: string;
  /** SHA-256 hash of the raw secret (hex); stable for the same secret value. */
  hash: string;
  /** Cryptographic key id used for envelope encryption at rest (audit only). */
  keyId?: string;
}

/**
 * A per-subaccount connection record. This is the internal shape; API responses
 * never include `secretHash` or any decrypted token — they return
 * `RedactedCredential` + status. `decryptKey` is never on the wire.
 */
export interface TenantConnection {
  /** Stable id, typically == locationId (one active connection per location). */
  id: string;
  /** The Full Funnel / HighLevel location id this credential scopes to. */
  locationId: string;
  /** Location display name (from the connection test / location lookup). */
  locationName?: string;
  /** Current lifecycle status. */
  status: ConnectionStatus;
  /** The credential in masked form (never the raw value). */
  credential: RedactedCredential;
  /** Scopes verified during the last successful connection test. */
  scopes: string[];
  /** Missing scopes after the last test (drives a "re-authorize" prompt). */
  missingScopes: string[];
  connectedAt?: ISODate;
  /** When the credential was last rotated (re-supplied by the admin). */
  rotatedAt?: ISODate;
  lastTestedAt?: ISODate;
  /** Human-readable error from the last test, when status === "error". */
  lastError?: string;
  revokedAt?: ISODate;
}

/** Request body for POST /api/v1/integration/connections (connect/rotate). */
export interface ConnectRequest {
  /** The Full Funnel location id to scope this credential to. */
  locationId: string;
  /**
   * The raw API key / private-integration token supplied by the subaccount
   * admin. NEVER persisted in plaintext, logged, or returned in any response.
   * The server hashes + encrypts it immediately on receipt.
   */
  secret: string;
  /** Optional display name override for the location. */
  locationName?: string;
}

/** Request body for POST /api/v1/integration/connections/:id/test. */
export interface ConnectionTestRequest {
  /** When true, re-run the scope check even if status is already "connected". */
  force?: boolean;
}

/** Result of a connection test (live probe against the FF API / mock). */
export interface ConnectionTestResult {
  ok: boolean;
  /** Verified scopes (subset of REQUIRED_FF_SCOPES the credential grants). */
  scopes: string[];
  /** Scopes from REQUIRED_FF_SCOPES that the credential is missing. */
  missingScopes: string[];
  /** Location display name resolved from the FF API (for the UI). */
  locationName?: string;
  /** Machine-readable failure reason when ok=false. */
  reason?: "invalid_credential" | "insufficient_scopes" | "unreachable" | "location_mismatch";
  /** Human-readable message (no secret values). */
  message?: string;
  testedAt: ISODate;
}

/**
 * The tenant connection lifecycle boundary (FUL-128). Behind this interface:
 * mock now (in-memory, hashes the secret for display), real when the encrypted
 * store + FF API proxy are wired at the gate. No consumer depends on the
 * storage mechanism or credential format.
 */
export interface ConnectionManager {
  /** Connect (or rotate) a credential for a location. Returns the redacted record. */
  connect(req: ConnectRequest): Promise<TenantConnection>;
  /** Get the redacted connection record for a location (null if not connected). */
  get(locationId: string): Promise<TenantConnection | null>;
  /** List all connection records (redacted), newest-first. */
  list(): Promise<TenantConnection[]>;
  /** Test a connection against the FF API / mock. Updates status + lastError. */
  test(locationId: string, opts?: ConnectionTestRequest): Promise<ConnectionTestResult>;
  /** Revoke (disconnect) a location's credential. Idempotent. */
  revoke(locationId: string): Promise<boolean>;
}

/* =====================================================================================
 * 15. Per-subaccount credential vault — audit + redacted error boundary (FUL-246)
 *
 * Every credential lifecycle action (connect, rotate, revoke, test, error) is
 * recorded as an immutable audit event. Audit events NEVER carry the raw secret,
 * the masked display, or any value derived from the secret beyond the SHA-256
 * hash already on the TenantConnection. This is the compliance/forensics trail
 * for per-tenant credential access — stored append-only per location.
 *
 * Provider errors returned to the API layer MUST be redacted: no token fragment,
 * request header, or provider response body leaks. Only a machine-readable
 * reason code + a human-readable safe message survive to the response.
 * ===================================================================================== */

/** Credential lifecycle action recorded in the audit trail. */
export type CredentialAuditAction =
  | "created"
  | "rotated"
  | "revoked"
  | "tested"
  | "connection_error"
  | "scope_validated";

/** A credential lifecycle audit event (append-only, never carries the secret). */
export interface CredentialAuditEvent {
  /** Stable id (UUID or derived `{locationId}:{action}:{at}`). */
  id: ID;
  /** The Full Funnel location id this audit event pertains to. */
  locationId: string;
  action: CredentialAuditAction;
  /** Connection status immediately after this action. */
  status: ConnectionStatus;
  at: ISODate;
  /** Safe human-readable detail (no secret fragments). */
  detail?: string;
  /** Missing scopes, when the action is scope_validated/tested. */
  missingScopes?: string[];
}

/**
 * The redacted error boundary: provider API failures are mapped to this shape
 * before crossing into any API response or log. NEVER embeds the raw provider
 * message, token fragment, or request detail — only a safe code + message.
 */
export interface RedactedProviderError {
  /** Machine-readable reason code (never includes token/header data). */
  reason: "invalid_credential" | "insufficient_scopes" | "unreachable" | "location_mismatch";
  /** Safe human-readable message (generic, no secret-derived data). */
  message: string;
  testedAt: ISODate;
}
